// Mantine
import {
    Button,
    NumberInput,
    Modal,
    Switch,
    ActionIcon,
    Tooltip,
    Popover,
} from '@mantine/core'

import { DatePickerInput } from '@mantine/dates'

// React
import { useState } from 'react'

// UI
import ComponentSelect from '../ui/ComponentSelect2'
import PurchaseOrderLineEdit from './PurchaseOrderLineEdit'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// Lodash sortBy
import sortBy from 'lodash/sortBy'

// Lodash sumBy
import sumBy from 'lodash/sumBy'
import groupBy from 'lodash/groupBy'

// Icons
import { IconCalendarEvent, IconTrash } from '@tabler/icons-react'

// Dayjs
import dayjs from 'dayjs'

// UI
import SKU from '../ui/SKU'

// Line Adding Component
const PurchaseOrderLineAdding = ({
    component,
    ratio,
    onQuantityChange,
    onToAddChange,
}) => {
    return (
        <div className="flex w-full items-center justify-between">
            <div className="flex flex-col">
                <span>
                    {component?.component_category?.name_fr} —{' '}
                    {component?.name}
                </span>

                {ratio && (
                    <span className="text-gray-400">
                        {ratio} par composant parent
                    </span>
                )}
            </div>

            <div className="flex items-center gap-3">
                <NumberInput
                    w={100}
                    value={component?.quantity_to_add}
                    onChange={onQuantityChange}
                />

                <Switch checked={component?.to_add} onChange={onToAddChange} />
            </div>
        </div>
    )
}

export default function PurchaseOrderLinesEdit({ purchaseOrder, handlers }) {
    // Lines
    const lines = purchaseOrder.lines

    // On component select
    const { requestGet } = useAxiosApi()

    const onComponentSelect = async (component) => {
        const { data } = await requestGet(`/components/${component.id}/children`)

        if (data) {
            // Add quantity to add to data
            data.quantity_to_add = 1
            data.to_add = false

            // Check if the component has children components

            if (data.children.length === 0) {
                confirmAddComponent(data)
                return
            }

            // Sort children by name
            data.children = sortBy(data.children, [
                (child) => child.component_category.name_fr + child.name,
            ])

            data.children.forEach((child) => {
                child.quantity_to_add = child.pivot.quantity
                child.to_add = true
            })

            setAddingComponent(data)
            setAddingComponentModalOpened(true)
        }
    }

    // Component being added
    const [addingComponent, setAddingComponent] = useState(null)

    const [isAddingComponentModalOpened, setAddingComponentModalOpened] =
        useState(false)

    const onQuantityChange = (component, quantity, isParent = false) => {
        if (isParent) {
            component.quantity_to_add = quantity

            component.children.forEach((child) => {
                child.quantity_to_add = child.pivot.quantity * quantity
            })
        } else {
            component.quantity_to_add = quantity
        }

        setAddingComponent({ ...addingComponent })
    }

    const onToAddChange = (component) => {
        component.to_add = !component.to_add

        setAddingComponent({ ...addingComponent })
    }

    const componentToAddCount =
        (addingComponent?.to_add ? 1 : 0) +
        sumBy(addingComponent?.children, (child) => (child.to_add ? 1 : 0))

    const confirmAddComponent = (data) => {
        // If this is a direct call
        if (data?.id) {
            handlers.addLine(data)
        } else {
            // Parent component
            if (addingComponent?.to_add) {
                // Check if the component is already in the list of lines
                handlers.addLine(addingComponent)
            }

            // Children components
            addingComponent?.children.forEach((child) => {
                if (child.to_add === true) {
                    // Check if the component is already in the list of lines
                    handlers.addLine(child)
                }
            })
        }

        // Close the modal
        setAddingComponentModalOpened(false)
    }

    // Grouped lines
    const groupedLines = sortBy(
        Object.entries(groupBy(lines, 'reception_at')).map((group) => {
            // Ordered lines by component name
            const orderedLines = sortBy(group[1], [
                (line) =>
                    line?.component?.component_category?.name_fr +
                    line.component.name,
            ])

            return [group[0], orderedLines]
        }),
        [(group) => (group[0] === 'null' ? '9999-12-31' : group[0])]
    )

    // Date group change date modal
    const [dateGroupChangeModalOpened, setDateGroupChangeModalOpened] =
        useState(false)

    const [dateGroupChangeDate, setDateGroupChangeDate] = useState(null)
    const [newReceptionDate, setNewReceptionDate] = useState(null)

    const openDateGroupChangeModal = (date) => {
        setDateGroupChangeDate(date)
        setNewReceptionDate(date !== 'null' ? dayjs(date).toDate() : null)
        setDateGroupChangeModalOpened(true)
    }

    const closeDateGroupChangeModal = () => {
        setDateGroupChangeModalOpened(false)
    }

    const confirmDateGroupChange = () => {
        // Change the date of all the lines in the group

        const formattedDate = dayjs(newReceptionDate).format('YYYY-MM-DD')

        handlers.changeDateGroup(dateGroupChangeDate, formattedDate)
        setDateGroupChangeModalOpened(false)
    }

    return (
        <>
            <Modal
                onClose={() => setAddingComponentModalOpened(false)}
                opened={isAddingComponentModalOpened}
                size="xl"
                title="Ajouter une ligne de bon de commande"
            >
                <div className="flex flex-col gap-5">
                    <div className="flex flex-col gap-3">
                        <span className="font-medium">Composant de base</span>

                        <div className="flex flex-col gap-2">
                            <PurchaseOrderLineAdding
                                component={addingComponent}
                                onQuantityChange={(quantity) =>
                                    onQuantityChange(
                                        addingComponent,
                                        quantity,
                                        true
                                    )
                                }
                                onToAddChange={() =>
                                    onToAddChange(addingComponent)
                                }
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-3">
                        <span className="font-medium">Éléments</span>

                        <div className="flex flex-col border">
                            {addingComponent?.children.map((child) => (
                                <div
                                    key={child.id}
                                    className="odd:bg-gray-50 p-3"
                                >
                                    <PurchaseOrderLineAdding
                                        component={child}
                                        ratio={child.pivot.quantity}
                                        onQuantityChange={(quantity) =>
                                            onQuantityChange(child, quantity)
                                        }
                                        onToAddChange={() =>
                                            onToAddChange(child)
                                        }
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <Button fullWidth onClick={confirmAddComponent}>
                        Ajouter les {componentToAddCount} composants
                    </Button>

                    <span className="text-gray-500 text-center">
                        Les composants déjà présents seront additionnées
                    </span>
                </div>
            </Modal>

            <Modal
                opened={dateGroupChangeModalOpened}
                onClose={closeDateGroupChangeModal}
                title="Changer les dates de réception"
            >
                <div className="flex flex-col gap-3">
                    <DatePickerInput
                        value={newReceptionDate}
                        onChange={setNewReceptionDate}
                        label="Date de réception"
                        placeholder="Date de réception"
                        description="La date la plus pessimiste de réception des composants"
                        valueFormat="LL"
                        minDate={new Date()}
                        locale="fr"
                    />

                    <Button fullWidth onClick={confirmDateGroupChange}>
                        Enregistrer la date
                    </Button>
                </div>
            </Modal>

            <div className="">
                <span className="font-medium">Composants attendus</span>

                <div className="my-3">
                    {groupedLines.map((group) => {
                        const reception_at = group[0]
                        const group_lines = group[1]

                        return (
                            <div
                                key={reception_at}
                                className="border p-5 mt-5 flex flex-col"
                            >
                                <div className="mb-5 flex w-full justify-between">
                                    <span className="font-medium mb-3">
                                        {reception_at != 'null'
                                            ? dayjs(reception_at).format('LL')
                                            : 'Date à déterminer'}
                                    </span>

                                    <Tooltip label="Changer la date de réception de tous les composants à cette date">
                                        <ActionIcon
                                            variant="default"
                                            onClick={() =>
                                                openDateGroupChangeModal(
                                                    reception_at
                                                )
                                            }
                                        >
                                            <IconCalendarEvent size="1rem" />
                                        </ActionIcon>
                                    </Tooltip>
                                </div>

                                <div className="flex flex-col gap-3">
                                    {group_lines.map((line, index) => (
                                        <PurchaseOrderLineEdit
                                            key={line.id ?? `new-${line.component_id}`}
                                            lines={lines}
                                            line={line}
                                            handlers={handlers}
                                        />
                                    ))}
                                </div>
                            </div>
                        )
                    })}
                </div>

                <ComponentSelect
                    label="Ajouter une ligne"
                    onSelect={onComponentSelect}
                />
            </div>
        </>
    )
}
